import { Body, Box, Heading, Icon, IconButton } from '@hover/blueprint';
import {
  iMoreHorizontal,
  iconArrowBack,
  iconArrowForward,
  iconChevronDown,
  iconChevronLeft,
  iconHelpOutlined,
  iconShare,
} from '@hover/icons';
import React, { useContext } from 'react';
import type { TransitionFn } from 'react-spring';
import { useTransition } from 'react-spring';

import { UIKind } from 'src/modules/designStudio/components/DesignStudio/UILayer/types';
import { OfflineButtonWrapper } from 'src/modules/designStudio/components/NavBar/OfflineButtonWrapper';
import { LANDSCAPE_TOOLBAR_WIDTH } from 'src/modules/designStudio/components/Toolbar/toolBarConstants';
import { useDesignVersioning } from 'src/modules/designStudio/hooks/useDesignVersioning';
import {
  InteractiveRenderingStateContext,
  Mode,
  ModeContext,
  uiLayerContext,
} from 'src/modules/designStudio/providers';
import { useDesigns } from 'src/modules/designStudio/providers/SavedDesignsProvider/useDesigns';
import { NavButton } from 'src/shared/components/NavButton';
import { useTranslate } from 'src/shared/hooks';
import { useReactNativeWebView } from 'src/shared/hooks/useReactNativeWebView';
import { TranslationNamespace } from 'src/shared/hooks/useTranslate';
import { backButtonPressed } from 'src/shared/telemetry';

import { Menu } from '../../constants';
import { historyContext } from '../../providers';

interface NavBarProps {
  menu: Menu | null;
  setMenu: (menu: Menu | null) => void;
  isOnline: boolean;
  showUndoRedo?: boolean;
  showShare?: boolean;
  onSavedDesignsMenuOpen: () => void;
}

interface UndoRedoNavBarProps {
  setMenu: (menu: Menu | null) => void;
  isOnline: boolean;
  showUndoRedo?: boolean;
  showShare?: boolean;
  backButtonTransition: TransitionFn<
    boolean,
    {
      opacity: number;
    }
  >;
}

interface SavedDesignsContextBarProps {
  onSavedDesignsMenuOpen: () => void;
  backButtonTransition: TransitionFn<
    boolean,
    {
      opacity: number;
    }
  >;
}

const SavedDesignsContextBar = ({
  backButtonTransition,
  onSavedDesignsMenuOpen,
}: SavedDesignsContextBarProps) => {
  const [_isWebView, sendReactNativeMessage] = useReactNativeWebView();
  const t = useTranslate(TranslationNamespace.DESIGN_STUDIO);

  const { kind: uiKind } = useContext(uiLayerContext);
  const savedDesigns = useDesigns();
  const isLandscape = uiKind === UIKind.LANDSCAPE;

  const activeDesign =
    savedDesigns.savedDesigns[savedDesigns.activeDesignIndex];

  let activeDesignTag: string | null = null;
  if (savedDesigns.hasUnsavedChanges)
    activeDesignTag = t('nav.savedDesigns.newChanges');
  else if (activeDesign.isOriginal)
    activeDesignTag = t('nav.savedDesigns.original');

  const handleOpenSavedDesigns = () => {
    // eslint-disable-next-line no-console
    onSavedDesignsMenuOpen();
  };

  const handleEditSavedDesign = () => {
    // eslint-disable-next-line no-console
    console.log('delete/edit');
  };

  return (
    <Box
      alignItems="center"
      flexDirection="row"
      id="nav-bar"
      justifyContent="space-between"
      left={isLandscape ? `${LANDSCAPE_TOOLBAR_WIDTH}px` : 300}
      margin={100}
      paddingLeft={isLandscape ? 300 : 0}
      pointerEvents="none"
      position="absolute"
      right="calc(var(--hover-space-300) + env(safe-area-inset-right))"
      testId="saved-designs-toolbar"
      top={700}
    >
      {!isLandscape &&
        backButtonTransition((style, show) =>
          show ? (
            <NavButton
              icon={iconChevronLeft}
              onClick={() => sendReactNativeMessage('handleNativeBack')}
              style={style}
              testId="back-button"
              track={backButtonPressed}
            />
          ) : (
            <Box marginX={400} />
          ),
        )}
      <Box
        alignItems="center"
        cursor="pointer"
        flexDirection={isLandscape ? 'row' : 'column'}
        gap={200}
        marginLeft={isLandscape ? 600 : 0}
        onClick={handleOpenSavedDesigns}
        pointerEvents="auto"
      >
        <Box alignItems="center" flexDirection="row">
          <Heading marginBottom={0} size={500}>
            {activeDesign.name}
          </Heading>
          <Icon
            color="neutral"
            fill="minimal"
            icon={iconChevronDown}
            marginX={200}
            size="small"
          />
        </Box>
        <Box alignItems="center" flexDirection="row">
          <Body color="neutral.600" marginY={0}>
            {activeDesignTag}
          </Body>
        </Box>
      </Box>
      <Box pointerEvents="auto">
        <IconButton
          fill="minimal"
          icon={iMoreHorizontal}
          label="more"
          onClick={handleEditSavedDesign}
        />
      </Box>
    </Box>
  );
};

const UndoRedoNavBar = ({
  setMenu,
  isOnline,
  showUndoRedo = true,
  showShare = true,
  backButtonTransition,
}: UndoRedoNavBarProps) => {
  const [_isWebView, sendReactNativeMessage] = useReactNativeWebView();

  const { undoDesignChange, redoDesignChange, canRedo, canUndo } =
    useContext(historyContext);
  const { setShowHelpOverlay } = useContext(InteractiveRenderingStateContext);
  const { kind: uiKind } = useContext(uiLayerContext);
  const { mode } = useContext(ModeContext);

  const t = useTranslate(TranslationNamespace.DESIGN_STUDIO);
  const isLandscape = uiKind === UIKind.LANDSCAPE;

  return (
    <Box
      flexDirection="row"
      id="nav-bar"
      justifyContent="space-between"
      left={isLandscape ? `${LANDSCAPE_TOOLBAR_WIDTH}px` : 300}
      margin={100}
      paddingLeft={isLandscape ? 300 : 0}
      pointerEvents="none"
      position="absolute"
      right="calc(var(--hover-space-300) + env(safe-area-inset-right))"
      top={700}
    >
      <>
        <Box>
          {backButtonTransition((style, show) =>
            show ? (
              <NavButton
                icon={iconChevronLeft}
                onClick={() => sendReactNativeMessage('handleNativeBack')}
                style={style}
                testId="back-button"
                track={backButtonPressed}
              />
            ) : (
              <Box />
            ),
          )}
        </Box>

        {mode === Mode.DEFAULT && (
          <Box gridColumnGap={200}>
            {showUndoRedo && (
              <>
                <OfflineButtonWrapper isOnline={isOnline}>
                  <NavButton
                    icon={iconArrowBack}
                    isDisabled={!isOnline || !canUndo}
                    label={t('undoRedo.undoButtonLabel')}
                    onClick={undoDesignChange}
                    testId="undo-button"
                  />
                </OfflineButtonWrapper>
                <OfflineButtonWrapper isOnline={isOnline}>
                  <NavButton
                    icon={iconArrowForward}
                    isDisabled={!isOnline || !canRedo}
                    label={t('undoRedo.redoButtonLabel')}
                    onClick={redoDesignChange}
                  />
                </OfflineButtonWrapper>
              </>
            )}
            {showShare && (
              <OfflineButtonWrapper isOnline={isOnline}>
                <NavButton
                  icon={iconShare}
                  isDisabled={!isOnline}
                  label={t('share.shareButtonLabel')}
                  onClick={() => setMenu(Menu.SHARE)}
                  testId="share"
                />
              </OfflineButtonWrapper>
            )}
          </Box>
        )}
        {mode === Mode.INTERACTIVE_RENDERING && (
          <Box>
            <NavButton
              icon={iconHelpOutlined}
              label={t('nav.interactiveRenderings.help')}
              onClick={() => setShowHelpOverlay(true)}
            />
          </Box>
        )}
      </>
    </Box>
  );
};

const NavBar = (props: NavBarProps) => {
  const shouldUseDesignVersioningNavBar = useDesignVersioning();
  const { menu, onSavedDesignsMenuOpen } = props;

  const [isWebView] = useReactNativeWebView();
  const backButtonTransition = useTransition(!menu && isWebView, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: {
      duration: 200,
    },
  });

  return shouldUseDesignVersioningNavBar ? (
    <SavedDesignsContextBar
      backButtonTransition={backButtonTransition}
      onSavedDesignsMenuOpen={onSavedDesignsMenuOpen}
    />
  ) : (
    <UndoRedoNavBar backButtonTransition={backButtonTransition} {...props} />
  );
};

export { NavBar };
